// Google Tag Manager loads only after analytics or marketing consent.
// Consent Mode v2 defaults to denied before gtm.js is inserted.

declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
    gtm?: boolean;
  }
}

export const GTM_ID = 'GTM-KCHMGC2W';

/** Signals required by Google Consent Mode v2. Non-essential storage starts denied. */
export const GTM_CONSENT_DENIED = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  functionality_storage: 'denied',
  personalization_storage: 'denied',
  security_storage: 'granted',
  wait_for_update: 500,
} as const;

export interface GtmConsentChoice {
  analytics: boolean;
  marketing: boolean;
}

let consentDefaultInstalled = false;

export function shouldLoadGtm(consent: GtmConsentChoice | null): boolean {
  return !!consent && (consent.analytics || consent.marketing);
}

export function resetGtmForTests(): void {
  consentDefaultInstalled = false;
  window.gtm = undefined;
  window.dataLayer = [];
  window.gtag = undefined;
}

function ensureGtag(): void {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // GTM reads the Arguments object, not a rest-parameter array.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }
}

/** Push Consent Mode default denied. Must run before the gtm.js tag. */
export function installGtagConsentDefault(): void {
  ensureGtag();
  if (consentDefaultInstalled) return;
  window.gtag?.('consent', 'default', { ...GTM_CONSENT_DENIED });
  consentDefaultInstalled = true;
}

export function updateGTMConsent(consent: GtmConsentChoice): void {
  installGtagConsentDefault();
  window.gtag?.('consent', 'update', {
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    ad_storage: consent.marketing ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
  });
}

/**
 * Insert gtm.js. Call only after installGtagConsentDefault().
 * Development skips the network request unless a test opts in.
 */
export function initializeGTM(options?: { allowInDev?: boolean }): void {
  installGtagConsentDefault();

  if (import.meta.env.DEV && !options?.allowInDev) {
    return;
  }

  if (window.gtm) {
    return;
  }

  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
    script.dataset.citysage = 'gtm';

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }

    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);

    if (document.body.firstChild) {
      document.body.insertBefore(noscript, document.body.firstChild);
    } else {
      document.body.appendChild(noscript);
    }

    window.gtm = true;
  } catch (error) {
    console.error('GTM: Failed to initialize', error);
  }
}

/** Load the container only when analytics or marketing consent is on. */
export function syncGtmWithConsent(
  consent: GtmConsentChoice | null,
  options?: { allowInDev?: boolean }
): void {
  if (!shouldLoadGtm(consent)) {
    if (window.gtm || consentDefaultInstalled) {
      updateGTMConsent({ analytics: false, marketing: false });
    }
    return;
  }

  updateGTMConsent(consent as GtmConsentChoice);
  initializeGTM(options);
}

/**
 * Push events to GTM dataLayer (with safety checks).
 * Does not load gtm.js.
 */
export function gtmPush(data: Record<string, unknown>): void {
  if (import.meta.env.DEV) {
    console.log('GTM Event (dev mode):', data);
    return;
  }

  if (window.dataLayer && window.gtm) {
    window.dataLayer.push(data);
  }
}
